import { useCallback, useContext, useRef, useState } from 'react';
import Dropzone from 'react-dropzone';

import ajax, { UnsupportedUploadTypeError } from './ajax';
import MediaViewer from './MediaViewer';
import Modal from 'react-modal';
import Cropper from 'react-easy-crop';
import { getCroppedImg } from '../logic/cropImage';
import { debounce } from 'lodash';
import { faClose } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { InfluencerIdContext } from '@client/new/contexts/InfluencerIdContext';
import { maxFileSizeBytes } from '@client/new/common/videos/videos.constants';
import * as styles from './media.css';
import type { MediaVariant } from './MediaViewer';
import { clsx } from 'clsx';

type MediaEditorProps = {
  id?: unknown;
  onChange: any; // two type signatures
  accept?: string;
  metadata: unknown;
  onRemove?: any;
  onDelete?: any;
  format: any;
  variant?: MediaVariant;
  crop?: boolean;
  width?: string;
  height?: string;
  aspect?: number;
};
type MediaEditorState = {
  files?: Array<File>;
  preview: null | unknown;
  message: string;
  inProgress: boolean;
  cropModal: boolean;
  croppedImageUrl: null | string;
};
const INITIAL_STATE: Readonly<MediaEditorState> = {
  files: [],
  preview: null,
  message: 'Drop or',
  inProgress: false,
  cropModal: false,
  croppedImageUrl: null,
};

const MediaEditor = (props: MediaEditorProps) => {
  const context = useContext(InfluencerIdContext);
  const dropzoneRef = useRef<any>(null);
  const [state, setState] = useState<MediaEditorState>(INITIAL_STATE);

  const merge = (partial: Partial<MediaEditorState>) =>
    setState((prev) => ({ ...prev, ...partial }));

  const onOpenClick = () => {
    dropzoneRef.current?.open();
  };

  const onDrop = (files: File[]) => {
    if (!files[0]) return;

    const videoSizeLimit = maxFileSizeBytes;

    if (files[0].size > videoSizeLimit) {
      merge({
        files: [],
        message: 'File too large. Max file size is 100MB',
        inProgress: false,
      });
      return;
    }

    const croppingEnabled = props.crop;
    merge({
      files: files,
      message: 'Uploading...',
      inProgress: true,
    });

    if (croppingEnabled) {
      merge({ cropModal: croppingEnabled });

      return;
    }

    onDropContinue(files);
  };

  const onCropCancel = () => {
    setState({ ...INITIAL_STATE });
  };

  const onCropSave = async () => {
    const croppedImageUrl = state.croppedImageUrl;

    if (!croppedImageUrl) {
      return;
    }
    merge({ cropModal: false });

    const newBlob = await fetch(croppedImageUrl).then((r) => r.blob());
    const oldFile = state.files?.[0];
    if (!oldFile) {
      return;
    }

    onDropContinue([new File([newBlob], oldFile.name, oldFile)]);
  };

  const onDropContinue = (files: File[]) => {
    if (props.id) props.onChange(props.id, null);
    else props.onChange(null);
    const influencerId = context;

    const isVideo = files[0].type.startsWith('video');

    ajax
      .upload(files[0], influencerId, { skipCompression: isVideo })
      .then((metadata) => {
        //console.log('[DropMediaEditor] Uploaded file', f);
        merge({
          files: files,
          message: 'Drop or',
          inProgress: false,
        });
        if (props.id) props.onChange(props.id, metadata);
        else props.onChange(metadata);
      })
      .catch((e) => {
        if (e instanceof UnsupportedUploadTypeError) {
          merge({ files: [], message: e.message, inProgress: false });
          return;
        }
        //if too large
        if (e.status === 413) {
          merge({
            files: [],
            message: 'File too large. Please drop again or',
            inProgress: false,
          });
          return;
        } else if (e.status === 400) {
          merge({
            files: [],
            message: 'Unsupported file format. Please drop again or',
            inProgress: false,
          });
          return;
        }
        console.log('[MediaEditor] Error uploading file.', e);
        merge({
          files: [],
          message: 'Woops, we encountered an error. Please drop again or',
          inProgress: false,
        });
      });
  };

  const isValidContext = typeof context === 'number' && context >= 0;
  if (!isValidContext) {
    throw new Error(
      'InfluencerIdContext is not set! Make sure to wrap this MediaEditor with InfluencerIdProvider.',
    );
  }
  const browseLink = state.inProgress ? (
    ''
  ) : (
    <a onClick={onOpenClick} className={styles.browseLink}>
      browse.
    </a>
  );
  const deleteLink =
    props.onDelete || (props.onRemove && props.metadata) ? (
      <a
        className={styles.removeIcon}
        onClick={() => {
          if (props.onDelete) {
            props.onDelete(props.id); // for multiple media editor
          } else if (props.onRemove) {
            props.onRemove(null);
          }
        }}
      ></a>
    ) : null;

  let content = (
    <span className={styles.dropzoneBanner}>
      {state.message}&nbsp;
      {browseLink}
    </span>
  );
  if (props.metadata) {
    content = (
      <div className={styles.editorPreview}>
        <MediaViewer
          variant={props.variant}
          metadata={props.metadata}
          width={props.width}
          height={props.height}
        />
      </div>
    );
  } else if (props.format) {
    content = (
      <span
        className={clsx(
          styles.dropzoneBanner,
          props.format === 'video'
            ? styles.dropzoneBannerVideo
            : styles.dropzoneBannerImage,
        )}
      >
        {state.message}&nbsp;
        {browseLink}
      </span>
    );
  }

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <Modal
        isOpen={state.cropModal}
        onRequestClose={onCropCancel}
        contentLabel="Example Modal"
        style={{
          overlay: {
            zIndex: 100,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          },
          content: {
            paddingRight: '40px',
            paddingLeft: '40px',
            paddingBottom: '40px',
            backgroundColor: 'black',
            position: 'relative',
            width: '80vw',
            height: '60vh',
            borderRadius: '20px',
          },
        }}
      >
        <a
          onClick={onCropCancel}
          className="absolute right-6 text-3xl text-white hover:text-primary hover:cursor-pointer"
        >
          <FontAwesomeIcon icon={faClose} />
        </a>
        <div className="text-2xl text-white text-center font-normal mb-4">
          Move the image to fit into highlighted area
        </div>
        <div
          className="relative"
          style={{
            width: '100%',
            height: 'calc(100% - 140px)',
            // background: 'white',
          }}
        >
          <CropImage
            src={(state.files?.[0] as any)?.preview}
            onChange={(croppedImageUrl) => merge({ croppedImageUrl })}
            aspect={props.aspect}
          />
        </div>
        <div className="w-full text-center">
          <button
            onClick={onCropSave}
            disabled={!state.croppedImageUrl}
            className="bg-primary text-white rounded-lg text-2xl mt-8 p-4 px-20"
          >
            Upload
          </button>
        </div>
      </Modal>
      <Dropzone
        ref={dropzoneRef}
        onDrop={onDrop}
        className="dropzone"
        activeClassName="dropzone-active"
        disableClick={true}
        style={{ height: '100%', width: '100%' }}
        accept={props.accept}
        multiple={!props.crop}
      >
        {deleteLink}
        {content}
      </Dropzone>
    </div>
  );
};

export default MediaEditor;

function useEasyCrop({ src, aspect = 1, onChange }): Cropper['props'] {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [_croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [zoom, setZoom] = useState(1);

  const onChangeHandlerDebounced = useCallback(
    debounce(async (croppedAreaPixels) => {
      console.log('onChangeHandler', new Date());
      const croppedImage = await getCroppedImg(
        src,
        croppedAreaPixels,
        rotation,
      );
      onChange(croppedImage);
    }, 200),
    [],
  );

  const onCropComplete = useCallback(
    async (_croppedArea, croppedAreaPixels) => {
      setCroppedAreaPixels(croppedAreaPixels);
      onChange(null);
      // NOTE: most likely doesn't support rotation after cropping
      onChangeHandlerDebounced(croppedAreaPixels);
    },
    [],
  );

  // @ts-ignore
  return {
    image: src,
    crop,
    rotation,
    onCropChange: setCrop,
    onCropComplete,
    onRotationChange: setRotation,
    aspect,
    zoomWithScroll: true,
    onZoomChange: setZoom,
    zoom,
  };
}

function CropImage({ src, aspect = 1, onChange }: any) {
  const cropper = useEasyCrop({ src, aspect, onChange });

  return (
    <div>
      <Cropper {...cropper} />
    </div>
  );
}
